/**
 * SocialCapital.cc — public API documentation.
 *
 * Zero external dependencies. All examples are copy-paste-ready
 * against `${REACT_APP_BACKEND_URL}/api/sc/`.
 */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Copy, CheckCircle2, KeyRound, Shield, Zap, Repeat } from "lucide-react";
import SocialCapitalHeader, {
  BRAND_BLUE, BRAND_PURPLE, BRAND_GREEN,
} from "@/components/socialcapital/SocialCapitalHeader";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/sc`;


function CodeBlock({ id, code }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };
  return (
    <div
      className="relative rounded-lg overflow-hidden mb-4"
      style={{ background: "#0F172A", border: "1px solid #1E293B" }}
    >
      <button
        data-testid={`copy-${id}`}
        onClick={doCopy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white/80 hover:text-white bg-white/10"
      >
        {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre
        className="text-[12.5px] leading-[1.55] p-4 pt-9 overflow-x-auto text-slate-200 font-mono"
        style={{ margin: 0 }}
      >
{code}
      </pre>
    </div>
  );
}


function EndpointRow({ method, path, desc, testid }) {
  const methodStyles = {
    GET:    { color: "#065F46", bg: "rgba(16,185,129,0.10)" },
    POST:   { color: "#1D4ED8", bg: "rgba(59,130,246,0.10)" },
    PATCH:  { color: "#B45309", bg: "rgba(245,158,11,0.10)" },
    DELETE: { color: "#B91C1C", bg: "rgba(220,38,38,0.10)" },
  }[method] || {};
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-[90px_1fr] md:grid-cols-[110px_320px_1fr] gap-2 py-3 border-b text-[13px]"
      style={{ borderColor: "#F1F5F9" }}
    >
      <span
        className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-[11px] font-mono font-bold self-start"
        style={{ background: methodStyles.bg, color: methodStyles.color }}
      >
        {method}
      </span>
      <code className="font-mono text-[12.5px] break-all self-center"
             style={{ color: "#0F172A" }}>{path}</code>
      <span className="text-[13px] self-center" style={{ color: "#475569" }}>{desc}</span>
    </div>
  );
}


export default function SocialCapitalApiDocs() {
  return (
    <div
      data-testid="sc-docs"
      className="min-h-screen"
      style={{ background: "linear-gradient(180deg,#F5F8FF 0%,#F8FAFC 100%)" }}
    >
      <Helmet>
        <title>SocialCapital.cc — Public API for organisations</title>
      </Helmet>
      <SocialCapitalHeader />

      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-10 pb-4">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] mb-3"
             style={{ color: BRAND_PURPLE }}>
          Public API · v1
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight"
            style={{ color: "#0F172A" }}>
          Connect your organisation to the ledger.
        </h1>
        <p className="mt-4 text-[16px] max-w-[720px]"
           style={{ color: "#475569" }}>
          Submit verified community activities and evidence. The Social Capital
          engine calculates recognition. Two authentication flows, one canonical
          submit endpoint, an append-only ledger anyone can audit.
        </p>
      </section>

      {/* ── Three pillars ─────────────────────────────────────── */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-8 grid gap-4 md:grid-cols-3">
        <PillarCard icon={KeyRound} tint={BRAND_BLUE}
          title="Two auth flows"
          body="Simple X-API-Key for quick starts. OAuth2 client_credentials for enterprise deployments." />
        <PillarCard icon={Repeat} tint={BRAND_GREEN}
          title="Idempotent submits"
          body="Retries never double-credit. Pass the same external_activity_id or idempotency_key and we return the original activity." />
        <PillarCard icon={Shield} tint={BRAND_PURPLE}
          title="Auditable by anyone"
          body="Every ledger row is SHA-256 hash-chained. Public /ledger/verify/{scid} walks the chain end-to-end." />
      </section>

      {/* ── Auth: API key ─────────────────────────────────────── */}
      <Section title="1 · Authenticate with an API key" testid="section-apikey">
        <p>
          Your super-admin mints one API key per environment from the{" "}
          <a href="/sc/org" style={{ color: BRAND_BLUE }}>Organisations dashboard</a>.
          Keys look like <code>sc_live_...</code> (production) or{" "}
          <code>sc_test_...</code> (sandbox). Pass on every request:
        </p>
        <CodeBlock id="apikey-example" code={`curl ${BASE}/me \\
  -H "X-API-Key: sc_live_YOUR_KEY_HERE"`} />
      </Section>

      {/* ── Auth: OAuth2 ──────────────────────────────────────── */}
      <Section title="2 · Or exchange OAuth2 client credentials" testid="section-oauth">
        <p>
          For standards-compliant integrations, exchange a{" "}
          <code>client_id</code> + <code>client_secret</code> for a 1-hour
          bearer token via <code>POST /sc/oauth/token</code>:
        </p>
        <CodeBlock id="oauth-token" code={`curl -X POST ${BASE}/oauth/token \\
  -d "grant_type=client_credentials" \\
  -d "client_id=YOUR_CLIENT_ID" \\
  -d "client_secret=YOUR_CLIENT_SECRET"

# → { "access_token": "...", "token_type": "Bearer",
#     "expires_in": 3600,
#     "scope": "activities:submit activities:read members:read" }`} />
        <CodeBlock id="oauth-use" code={`curl ${BASE}/me \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`} />
      </Section>

      {/* ── Submit activity ───────────────────────────────────── */}
      <Section title="3 · Submit an activity" testid="section-submit">
        <p>
          The most-called endpoint. Report facts — <b>never points</b>. The
          engine calculates recognition based on category, verification level,
          quantity (hours or units), your org's trust level, and category-specific
          evidence bonuses.
        </p>
        <CodeBlock id="submit" code={`curl -X POST ${BASE}/activities \\
  -H "X-API-Key: sc_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_activity_id": "your-internal-id-42",
    "idempotency_key": "req-a71bf90",
    "participant": { "email": "member@example.org" },
    "category": "volunteer_service",
    "title": "Saturday food-bank shift",
    "description": "Sorted 220 kg of produce and served 60 families.",
    "activity_date": "2026-02-10",
    "hours": 4,
    "people_benefited": 60,
    "country_code": "US",
    "verified_by_organization": true,
    "verification_level_claimed": 3,
    "evidence": [
      { "kind": "photo", "url": "https://example.org/proof.jpg", "visibility": "verifier_only" }
    ]
  }'`} />
        <p className="mt-4 text-[13px]" style={{ color: "#64748B" }}>
          Response includes the minted <code>scid</code>, the calculated{" "}
          <code>social_capital_awarded</code>, the exact scoring breakdown,
          and a link into the ledger.
        </p>
      </Section>

      <Section title="4 · Batch submit (up to 500)" testid="section-batch">
        <CodeBlock id="batch" code={`curl -X POST ${BASE}/activities/batch \\
  -H "X-API-Key: sc_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "activities": [ /* ...up to 500 ActivityIn... */ ] }'`} />
      </Section>

      {/* ── Public reads ──────────────────────────────────────── */}
      <Section title="5 · Public ledger reads" testid="section-public-reads">
        <p>These endpoints are unauthenticated so third parties can independently verify a member's Social Capital record:</p>
        <div className="rounded-lg border bg-white p-4 mt-3" style={{ borderColor: "#E5E7EB" }}>
          <EndpointRow method="GET" path={`${BASE}/passport/{scid}`}
            desc="Balance, per-category breakdown, and last 20 ledger rows."
            testid="ep-passport" />
          <EndpointRow method="GET" path={`${BASE}/balance/{scid}`}
            desc="Just the derived balance."
            testid="ep-balance" />
          <EndpointRow method="GET" path={`${BASE}/ledger/{scid}?limit=50`}
            desc="Full ledger row list, newest first."
            testid="ep-ledger" />
          <EndpointRow method="GET" path={`${BASE}/ledger/verify/{scid}`}
            desc="Walks the SHA-256 hash chain end-to-end and reports {ok, rows_checked, broken_at}."
            testid="ep-verify" />
          <EndpointRow method="GET" path={`${BASE}/health`}
            desc="Green/red probe."
            testid="ep-health" />
        </div>
      </Section>

      {/* ── All endpoints ─────────────────────────────────────── */}
      <Section title="6 · Full endpoint map" testid="section-endpoints">
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: "#E5E7EB" }}>
          <EndpointRow method="POST"   path="/sc/oauth/token"                       desc="OAuth2 client-credentials → Bearer token"  testid="ep-token" />
          <EndpointRow method="GET"    path="/sc/me"                                desc="Resolve current org (from key or Bearer)" testid="ep-me" />
          <EndpointRow method="POST"   path="/sc/activities"                        desc="Submit a single activity"                  testid="ep-act-submit" />
          <EndpointRow method="POST"   path="/sc/activities/batch"                  desc="Submit up to 500 activities"               testid="ep-act-batch" />
          <EndpointRow method="GET"    path="/sc/activities/{id}"                   desc="Fetch one of your activities"              testid="ep-act-get" />
          <EndpointRow method="POST"   path="/sc/activities/{id}/verify"            desc="Approve or reject a pending activity"      testid="ep-act-verify" />
          <EndpointRow method="GET"    path="/sc/passport/{scid}"                   desc="Public passport aggregate"                 testid="ep-pass" />
          <EndpointRow method="GET"    path="/sc/balance/{scid}"                    desc="Derived balance"                           testid="ep-bal" />
          <EndpointRow method="GET"    path="/sc/ledger/{scid}"                     desc="Ledger history"                            testid="ep-hist" />
          <EndpointRow method="GET"    path="/sc/ledger/verify/{scid}"              desc="Hash-chain integrity check"                testid="ep-vfy" />
          <EndpointRow method="GET"    path="/sc/me/passport"                       desc="Signed-in user's own passport (SocialPulse cookie/session)" testid="ep-mine" />
        </div>
      </Section>

      {/* ── Scoring rules ─────────────────────────────────────── */}
      <Section title="7 · How points are calculated" testid="section-scoring">
        <p>
          The formula is public. Organisations report facts; the engine does
          the multiplication. No caller can inflate a score.
        </p>
        <div
          className="rounded-lg p-4 mt-3 font-mono text-[13px] leading-[1.6]"
          style={{ background: "#0F172A", color: "#E2E8F0" }}
        >
          points = base(category)
        <br />       × verification_multiplier(final_level)
        <br />       × quantity_multiplier(hours or units)
        <br />       × org_trust_multiplier(org.trust_level)
        <br />       × category_bonus(evidence)
        </div>
        <p className="mt-4">
          A per-category single-activity cap is applied last (300 default,
          400 for emergency assistance, 500 for disaster response) so a
          multi-day event never overwhelms the ledger.
        </p>
      </Section>

      {/* ── Footer CTA ────────────────────────────────────────── */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-10">
        <div
          className="rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5"
          style={{
            background: "linear-gradient(120deg,#EEF4FF 0%,#F5F0FF 100%)",
            border: "1px solid #E0E7FF",
          }}
        >
          <Zap className="w-8 h-8 shrink-0" style={{ color: BRAND_BLUE }} />
          <div className="flex-1">
            <h3 className="font-display text-xl mb-1" style={{ color: "#0F172A" }}>
              Ready to onboard your organisation?
            </h3>
            <p className="text-[14px]" style={{ color: "#475569" }}>
              Ask your SocialCapital super-admin to register your organisation
              from the <a href="/sc/org" style={{ color: BRAND_BLUE }}>Organisations dashboard</a>{" "}
              and mint your first sandbox key.
            </p>
          </div>
          <a
            href="/sc/org"
            data-testid="cta-org-dashboard"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold text-white shrink-0"
            style={{ background: BRAND_BLUE }}
          >
            Open dashboard
          </a>
        </div>
      </section>
    </div>
  );
}


function Section({ title, testid, children }) {
  return (
    <section
      data-testid={testid}
      className="max-w-[1100px] mx-auto px-5 lg:px-8 py-6"
    >
      <h2 className="font-display text-2xl mb-4" style={{ color: "#0F172A" }}>
        {title}
      </h2>
      <div className="text-[14px] leading-[1.65]" style={{ color: "#334155" }}>
        {children}
      </div>
    </section>
  );
}


function PillarCard({ icon: Icon, tint, title, body }) {
  return (
    <div
      className="rounded-xl border bg-white p-5"
      style={{ borderColor: "#E5E7EB" }}
    >
      <span
        className="inline-flex items-center justify-center w-9 h-9 rounded-md mb-3"
        style={{ background: `${tint}18`, color: tint }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="font-display text-[16px] mb-1" style={{ color: "#0F172A" }}>
        {title}
      </div>
      <p className="text-[13px] leading-[1.55]" style={{ color: "#64748B" }}>
        {body}
      </p>
    </div>
  );
}
